import dateUtils from "../../../_services/date-utils";

// Mirrors ShopifyWebhookHealthDto on the platform (issue #41971): a thin wrapper over the two existing
// webhook audits — platformAudit (app lifecycle topics, owned by the platform) and handlerAudit (order topics,
// owned by the webhook-handler). Shared by the Webhooks tab and its modal so neither has to import the other.
export const WEBHOOK_BUCKET = {
    CORRECT: "CORRECT",
    DUPLICATE: "DUPLICATE",
    MISSING: "MISSING",
    WRONG_PATH: "WRONG_PATH",
    WRONG_HOST: "WRONG_HOST",
    UNREACHABLE: "UNREACHABLE",
    // Not a backend value: what any bucket this mirror does not know collapses to (see effectiveBucket).
    UNKNOWN: "UNKNOWN",
};

// Which of the two writes on a credential's entry ran last, so the modal can word its spinner and its error for
// the action the user actually triggered. Declared in this leaf module (which imports nothing but date-utils)
// because both the reducer and the modal need it, and the modal must not import the reducer for it.
export const WebhookHealthAction = {
    CHECK: "CHECK",
    REPAIR: "REPAIR",
};

export const WEBHOOK_TOPIC_STATUS = {
    CORRECT: "CORRECT",
    MISSING: "MISSING",
    WRONG_PATH: "WRONG_PATH",
    WRONG_HOST: "WRONG_HOST",
    DUPLICATE: "DUPLICATE",
};

// Worst-first is what the merged pill has to show, so the ranks are ordered by how badly orders are affected.
const BUCKET_SEVERITY_RANK = [
    WEBHOOK_BUCKET.CORRECT,
    WEBHOOK_BUCKET.DUPLICATE,
    WEBHOOK_BUCKET.MISSING,
    WEBHOOK_BUCKET.WRONG_PATH,
    WEBHOOK_BUCKET.WRONG_HOST,
];

// The audits report topics in Shopify's GraphQL enum form; CS knows them by the REST names shown in the Shopify
// admin, so the table shows those.
const TOPIC_LABELS = {
    ORDERS_CREATE: "orders/create",
    ORDERS_UPDATED: "orders/updated",
    ORDERS_PAID: "orders/paid",
    ORDERS_DELETE: "orders/delete",
    ORDERS_CANCELLED: "orders/cancelled",
    ORDERS_PARTIALLY_FULFILLED: "orders/partially_fulfilled",
    ORDERS_FULFILLED: "orders/fulfilled",
    REFUNDS_CREATE: "refunds/create",
    DRAFT_ORDERS_CREATE: "draft_orders/create",
    DRAFT_ORDERS_UPDATE: "draft_orders/update",
    APP_SUBSCRIPTIONS_UPDATE: "app_subscriptions/update",
    APP_UNINSTALLED: "app/uninstalled",
};

export const topicLabel = (topic) => TOPIC_LABELS[topic] || (topic || "").toLowerCase();

const audits = (health) => [health.platformAudit, health.handlerAudit].filter(Boolean);

const findingsOf = (audit) => (audit && Array.isArray(audit.findings) ? audit.findings : []);

export const topicsOf = (health) => [...findingsOf(health.platformAudit), ...findingsOf(health.handlerAudit)];

const isUnreachable = (health) => audits(health).some((audit) => audit.bucket === WEBHOOK_BUCKET.UNREACHABLE);

const isKnownBucket = (bucket) => BUCKET_SEVERITY_RANK.includes(bucket) || bucket === WEBHOOK_BUCKET.UNREACHABLE;

/**
 * The worst bucket of the audits that answered, or null when the shop was never checked.
 *
 * A bucket this module does not know (a new backend value, a serialisation quirk) fails closed to UNKNOWN: the
 * ranking would otherwise keep its CORRECT seed and paint a green "all webhooks active" over a shop the backend
 * has just classified as broken, which is the one answer this tool must never give.
 */
export const effectiveBucket = (health) => {
    const present = audits(health);
    if (present.length === 0) {
        return null;
    }
    if (isUnreachable(health)) {
        return WEBHOOK_BUCKET.UNREACHABLE;
    }
    if (present.some((audit) => !isKnownBucket(audit.bucket))) {
        return WEBHOOK_BUCKET.UNKNOWN;
    }
    return present
        .map((audit) => audit.bucket)
        .reduce(
            (worst, bucket) =>
                BUCKET_SEVERITY_RANK.indexOf(bucket) > BUCKET_SEVERITY_RANK.indexOf(worst) ? bucket : worst,
            WEBHOOK_BUCKET.CORRECT
        );
};

// The order webhooks all point at the handler, so its first finding names the endpoint; the platform audit is
// only consulted when the handler audit is absent.
export const endpointOf = (health) => {
    const finding = findingsOf(health.handlerAudit)[0] || findingsOf(health.platformAudit)[0];
    return finding ? finding.expectedUrl : null;
};

// Top-level note first, then per-audit notes, so CS can tell whether Shopify or the webhook-handler failed.
export const notesOf = (health) =>
    [health.note, ...audits(health).map((audit) => audit.note)].filter(
        (note, index, all) => typeof note === "string" && note.trim() !== "" && all.indexOf(note) === index
    );

// Null when the shop was never checked: a pill in any of the three tints would claim a status that does not
// exist, so the caller renders plain text instead.
export const webhookHealthPill = (health) => {
    const bucket = effectiveBucket(health);
    if (!bucket) {
        return null;
    }
    if (bucket === WEBHOOK_BUCKET.UNREACHABLE) {
        return { severity: "error", label: "Nicht erreichbar" };
    }
    if (bucket === WEBHOOK_BUCKET.UNKNOWN) {
        return { severity: "warning", label: "Unbekannter Status" };
    }
    const topics = topicsOf(health);
    const correct = topics.filter((topic) => topic.status === WEBHOOK_TOPIC_STATUS.CORRECT).length;
    return {
        severity: bucket === WEBHOOK_BUCKET.CORRECT ? "ok" : "warning",
        label: `${correct} von ${topics.length} aktiv`,
    };
};

const JUST_CHECKED_MS = 60 * 1000;

// Derived from the checkedAt the DTO carries, never from a flag on the entry: a sticky "checked just now" would
// still claim a fresh answer hours later, and after a failed re-check. Only a genuinely recent timestamp earns
// the relative wording — an unparseable one reads as never checked, and a future one is shown as it is rather
// than dressed up as fresh.
export const formatCheckedAt = (health) => {
    if (!health.checkedAt) {
        return "Nicht geprüft";
    }
    const checkedAt = new Date(health.checkedAt);
    if (Number.isNaN(checkedAt.getTime())) {
        return "Nicht geprüft";
    }
    const age = Date.now() - checkedAt.getTime();
    if (age >= 0 && age < JUST_CHECKED_MS) {
        return "gerade eben";
    }
    return dateUtils.formatToGermanDateTimeString(checkedAt, false);
};
